if (!customElements.get('ajax-cart')) {
  customElements.define(
    'ajax-cart',
    class AjaxCart extends HTMLElement {
      constructor() {
        super();

        if (this.notificationBanner && !this.notificationBanner.initialized) {
            this.notificationBanner.initialized = true;
            this.notificationBanner.connectedCallback?.();
        }
      }

      connectedCallback() {
        this.isOpen = false;

        const tasks = [
          this.setConfiguration.bind(this),
          this.bindDynamic.bind(this),
          this.bindStatic.bind(this),
          this.setSubscriptions.bind(this),
          this.setHeaderCartIconAccessibility.bind(this)
        ];

        this.yieldTasks(tasks);
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        this.removeEventListener('keyup', this.handleKeyUp);
        this.productContainer?.removeEventListener('change', this.debouncedOnChange);
        this.headerOpener?.removeEventListener('click', this.handleOpen);
        this.overlay?.removeEventListener('click', this.handleClose);
        this.headerCartLink?.removeEventListener('click', this.handleOpen);
        this.headerCartLink?.removeEventListener('keydown', this.handleKeyDown);
        this.closeButton?.removeEventListener('click', this.handleClose);
      }

      yieldToMain() {
        return new Promise((resolve) => {
          setTimeout(resolve, 0);
        });
      }

      async yieldTasks(tasks) {
        while (tasks.length > 0) {
          const task = tasks.shift();
          task();
          await this.yieldToMain();
        }
      }

      setConfiguration() {
        this.filterClass = '.ajax-cart__js-replace';

        this.headerCartLink = document.querySelector('#cart-icon-bubble');
        this.closeButton = this.querySelector('.ajax-cart__close');
        this.overlay = this.querySelector('.ajax-cart__overlay');

        this.handleClose = this.close.bind(this);
        this.handleOpen = this.open.bind(this);
        this.handleKeyUp = this.onKeyUp.bind(this);
        this.handleKeyDown = this.onKeyDown.bind(this);
        this.debouncedOnChange = debounce((event) => {
          this.onChange(event);
        }, ON_CHANGE_DEBOUNCE_TIMER).bind(this);
        this.submitHandler = this.onSubmit.bind(this);

        if (window.cartOptions?.notificationType !== 'slide-out') {
          this.cartNotificationModal = document.querySelector('cart-notification-modal');
          this.cartNotificationShade = document.querySelector('cart-notification');
        }
      }

      setSubscriptions() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'ajax-cart' ||
            (event.source === 'product-form' && this.cartNotificationModal)
          ) {
            return;
          }
          this.onCartUpdate();
        });
      }

      setHeaderCartIconAccessibility() {
        this.headerCartLink?.setAttribute('role', 'button');
        this.headerCartLink?.setAttribute('aria-haspopup', 'dialog');
        this.headerCartLink?.addEventListener('click', this.handleOpen);
        this.headerCartLink?.addEventListener('keydown', this.handleKeyDown);
      }

      setAccessibilityOptions() {
        if (this.isOpen) {
          this.setAttribute('aria-hidden', false);
          this.setAttribute('tabindex', 0);
        } else {
          this.setAttribute('aria-hidden', true);
          this.setAttribute('tabindex', -1);
        }
      }

      bindDynamic() {
        this.errorContainer = this.querySelector('.ajax-cart__errors');
        this.errorText = this.errorContainer?.querySelector('span');
        this.accessbilityStatus = this.querySelector('.ajax-cart__line-status');
        this.productContainer = this.querySelector('.ajax-cart__lines');
        this.productContainer?.addEventListener('change', this.debouncedOnChange);
        this.submitButton = this.querySelector('.ajax-cart__checkout');
        this.submitButton?.addEventListener('click', this.submitHandler);
        this.complementaryProducts = this.querySelector('.ajax-cart__complementary embla-carousel');
      }

      unbindDynamic() {
        this.complementaryIndex = this.complementaryProducts?.emblaApi?.slidesInView();
        this.productContainer?.removeEventListener('change', this.debouncedOnChange);
        this.submitButton?.removeEventListener('click', this.submitHandler);
      }

      bindStatic() {
        this.productContainer?.addEventListener('change', this.debouncedOnChange);
        this.headerOpener?.addEventListener('click', this.handleOpen);
        this.closeButton?.addEventListener('click', this.handleClose);
        this.addEventListener('keyup', this.handleKeyUp);
        this.overlay?.addEventListener('click', this.handleClose);
      }

      onSubmit(event) {
        event?.preventDefault();
        if (this.loading) return;
        const disabled = event?.target.getAttribute('disabled');
        if (disabled) return;
        this.submitButton.setAttribute('disabled', true);
        window.location.href = '/checkout';
      }

      onKeyUp(event) {
        event?.code === 'Escape' && this.close();
      }

      onKeyDown(event) {
        if (event?.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open();
        }
      }

      onChange(event) {
        if (!event) return;
        this.updateQuantity(
          event.target.dataset.index,
          event.target.value,
          event.target.getAttribute('name'),
          event.target.dataset.quantityVariantId
        );
      }

      onCartUpdate() {
        fetch(`${routes.cart_url}?section_id=p80-ajax-cart`)
          .then((response) => response.text())
          .then((responseText) => {
            this.replaceNodes(responseText, this.filterClass);
          })
          .catch((event) => {
            console.error(event);
          });
      }

      open(event) {
        event?.preventDefault();
        this.cartNotificationModal?.close();
        this.cartNotificationShade?.close();

        setTimeout(() => {
          this.isOpen = true;
          this.classList.add('ajax-cart--visible');
          document.body.classList.add('overflow-hidden');
          this.setAccessibilityOptions();
        });

        this.addEventListener(
          'transitionend',
          () => {
            trapFocus(this, this.closeButton);
          },
          { once: true }
        );
      }

      close() {
        this.isOpen = false;
        this.classList.remove('ajax-cart--visible');
        document.body.classList.remove('overflow-hidden');
        this.setAccessibilityOptions();
        this.resetStatus();
        removeTrapFocus();
      }

      replaceNodes(section, filter) {
        if (!section) return;
        const nodes = this.getSectionDOM(section, filter, true);
        if (!nodes.length) return;

        this.unbindDynamic();

        this.querySelectorAll(filter).forEach((node, index) => {
            if (
                typeof this.complementaryIndex !== 'undefined' &&
                node.classList.contains('ajax-cart__complementary')
            ) {
                const embla = nodes[index].querySelector('embla-carousel');
                embla?.setAttribute('data-start-at', this.complementaryIndex);
            }
            node.replaceWith(nodes[index]);
        });

        this.bindDynamic();

        // ⭐ IMPORTANT: Rebind new banner element after cart drawer refresh
        this.notificationBanner = this.querySelector('notification-banner');

        // ⭐ Re-run connectedCallback so JS fully initializes the component
        if (this.notificationBanner && !this.notificationBanner.initialized) {
            this.notificationBanner.initialized = true;
            this.notificationBanner.connectedCallback?.();
        }
      }

      renderContents(parsedState) {
        if (!parsedState) return;
        this.cartItemKey = parsedState.key;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (!sectionElement) {
            return;
          }
          if (section.type && section.type === 'DOM') {
            if (section.id === 'p80-ajax-cart') {
              this.replaceNodes(parsedState.sections[section.id], section.filter);
              return;
            }
            const node = this.getSectionDOM(parsedState.sections[section.id], section.selector);
            if (!node) return;

            sectionElement.replaceWith(node);
          } else {
            const html = this.getSectionInnerHTML(
              parsedState.sections[section.id],
              section.selector
            );
            if (!html) return;
            sectionElement.innerHTML = html;
          }
        });
        // I don't know what Shopify has done but cart returns are wholly inconsistent now
        // The key is required to reliably show the product in the popup modal, and randomly cart returns return nothing but section data
        // This is also a problem because that means absolutely 0 updated cart information gets returned from the endpoints
        // It might be related to Chrome's new cookie policies but then again I can replicate this on every browser (using stock Dawn 13.01)
        // If that data is not returned we just fall back to opening the main cart as it does not require any of this info and can do a full refresh of it's own
        // Context: https://github.com/p80w/p80-dawn/issues/171
        if (!this.cartItemKey && !this.isOpen) {
          this.open();
        } else if (
          window.cartOptions?.notificationType === 'modal' &&
          this.cartNotificationModal &&
          !this.isOpen
        ) {
          this.cartNotificationModal.open();
        } else if (!this.isOpen) {
          this.open();
        }
      }


updateQuantity(line, quantity, name, variantId) {
  if (this.loading) return;

  const ajaxCartRoot = this;
  const requestedQty = parseInt(quantity, 10) || 0;

  const customerCountry =
    (ajaxCartRoot.getAttribute('data-customer-country') || '').toUpperCase();

  // 🔁 Always refresh banner reference (important after re-render)
  this.notificationBanner =
    this.notificationBanner || this.querySelector('notification-banner');

  const quantityElement =
    document.getElementById(`ajax-cart__quantity--${line}`);

  const variantInput =
    document.querySelector(`[data-quantity-variant-id="${variantId}"]`) ||
    quantityElement;

  /* =================================================
     🇺🇸 US — PER PRODUCT LIMIT ONLY
     🌍 Rest of world — unlimited, same as US default
  ================================================= */
  const themeLimit =
    parseInt(ajaxCartRoot.getAttribute('data-default-max'), 10) || Infinity;

  let variantMax;
  if (variantInput?.dataset?.maxQty) {
    const parsed = parseInt(variantInput.dataset.maxQty, 10);
    if (!isNaN(parsed)) variantMax = parsed;
  }

  // US rule:
  // - if seed.max exists → use it
  // - if NOT → unlimited
  // Non-US rule: always unlimited (no international cap)
  let allowedMax;
  if (customerCountry === 'US') {
    allowedMax = variantMax ?? Infinity;
  } else {
    allowedMax = Infinity;
  }

  // ❌ Block if exceeds allowed
  if (requestedQty > allowedMax) {
    if (quantityElement && allowedMax !== Infinity) {
      quantityElement.value = allowedMax;
    }

    this.notificationBanner?.setCartValidationBanner(
      requestedQty,
      allowedMax === Infinity ? requestedQty : allowedMax,
      customerCountry
    );
    return; // ⛔ STOP
  }

  /* =================================================
     ✅ SAFE — PROCEED WITH CART UPDATE
  ================================================= */
  this.enableLoading(line);

  const body = JSON.stringify({
    line,
    quantity: requestedQty,
    sections: this.getSectionsToRender().map((section) => section.id),
    sections_url: window.location.pathname
  });

  fetch(`${routes.cart_change_url}`, {
    ...fetchConfig(),
    body
  })
    .then((response) => response.text())
    .then((state) => {
      const parsedState = JSON.parse(state);

      this.renderContents(parsedState);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'ajax-cart',
        cartData: parsedState,
        variantId
      });

      this.setAttribute(
        'data-items-count',
        parsedState.items.length
      );
    })
    .catch(console.error)
    .finally(() => {
      this.disableLoading(line);
    });
}





// helper


_proceedUpdate(line, quantity, name, variantId) {
  this.enableLoading(line);

  const body = JSON.stringify({
    line,
    quantity,
    sections: this.getSectionsToRender().map((section) => section.id),
    sections_url: window.location.pathname
  });

  fetch(`${routes.cart_change_url}`, {
    ...fetchConfig(),
    body
  })
    .then((response) => response.text())
    .then((state) => {
      const parsedState = JSON.parse(state);

      this.renderContents(parsedState);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'ajax-cart',
        cartData: parsedState,
        variantId
      });

      this.setAttribute(
        'data-items-count',
        parsedState.items.length
      );
    })
    .catch(console.error)
    .finally(() => {
      this.disableLoading(line);
    });
}




      enableLoading(line) {
        if (!line) return;
        this.loading = true;
        const cartItems = document.querySelector('.ajax-cart__products');
        cartItems?.classList.add('ajax-cart__products--disabled');

        const cartItemSpinners = this.querySelectorAll(
          `.ajax-cart__product--${line} .loading__spinner`
        );

        cartItemSpinners.forEach((overlay) => overlay.classList.remove('hidden'));
        this.querySelector('.ajax-cart__summary .loading__spinner')?.classList.add('hidden');

        document.activeElement.blur();
        this.accessbilityStatus?.setAttribute('aria-hidden', false);
        this.submitButton?.classList.add('disabled');
        this.submitButton?.setAttribute('disabled', true);
      }

      disableLoading(line) {
        if (!line) return;
        this.loading = false;
        const cartItems = document.querySelector('.ajax-cart__products');
        cartItems?.classList.remove('ajax-cart__products--disabled');

        const cartItemSpinners = this.querySelectorAll(`.ajax-cart__product .loading__spinner`);

        cartItemSpinners.forEach((overlay) => overlay.classList.add('hidden'));
        this.querySelector('.ajax-cart__summary .loading__spinner')?.classList.add('hidden');

        this.accessbilityStatus?.setAttribute('aria-hidden', true);
        this.submitButton?.classList.remove('disabled');
        this.submitButton?.removeAttribute('disabled');
      }

      updateStatus(message) {
        if (!this.accessbilityStatus || !message) return;
        this.accessbilityStatus.setAttribute('aria-hidden', false);
        this.accessbilityStatus.innerHTML = message;
      }

      resetStatus() {
        if (!this.accessbilityStatus) return;
        this.accessbilityStatus.setAttribute('aria-hidden', true);
        this.accessbilityStatus.textContent = '';
      }

      getSectionDOM(html, selector = '.shopify-section', all = false) {
        if (!html) {
          console.info('No markup passed to generate the DOM.');
          return;
        }
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        if (all) {
          return parsedHtml.querySelectorAll(selector);
        }
        return parsedHtml.querySelector(selector);
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        if (!html) {
          console.info('No markup passed to generate the HTML.');
          return;
        }
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        const matchingNode = parsedHtml.querySelector(selector);
        if (!matchingNode) return;
        return matchingNode.innerHTML;
      }

      getSectionsToRender() {
        return [
          {
            id: 'cart-notification-product',
            selector: `[id="cart-notification-product-${this.cartItemKey}"]`
          },
          {
            id: 'cart-notification-button'
          },
          {
            id: 'cart-icon-bubble'
          },
          {
            id: 'p80-ajax-cart',
            filter: this.filterClass,
            type: 'DOM'
          }
        ];
      }

      // Needed for stock Shopify components
      setActiveElement() {}
    }
  );
}

class AjaxCartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('ajax-cart');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('ajax-cart-remove-button', AjaxCartRemoveButton);

if (!customElements.get('ajax-cart-note')) {
  customElements.define(
    'ajax-cart-note',
    class AjaxCartNote extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.textArea = this.querySelector('textarea');
        if (!this.textArea) {
          this.classList.add('hidden');
          return;
        }
        this.note = this.textArea.value;
        this.onInputHandler = debounce((event) => {
          this.onInput(event);
        }, ON_CHANGE_DEBOUNCE_TIMER).bind(this);
        this.addEventListener('input', this.onInputHandler);
      }

      disconnectedCallback() {
        this.removeEventListener('input', this.onInputHandler);
      }

      onInput(event) {
        const body = JSON.stringify({ note: this.textArea?.value });
        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body }
        })
          .then((response) => response.json())
          .then((response) => {
            this.note = response.note;
            this.onUpdate();
          })
          .catch((event) => {
            console.error(event);
          });
      }

      onUpdate() {
        const input = document.getElementById('ajax-cart__note-status');
        if (!input) return;
        if (this.note) {
          input.checked = true;
        } else {
          input.checked = false;
        }
      }
    }
  );
}

if (typeof AccordionItem === 'function' && typeof AccordionNoteItem !== 'function') {
  class AccordionNoteItem extends AccordionItem {
    constructor(element) {
      super(element);
    }

    onClick(event) {
      super.onClick(event);
      this.toggleCheckbox();
    }

    onKeyDown(event) {
      if (event?.code.toUpperCase() === 'SPACE' || event?.code.toUpperCase() === 'ENTER') {
        super.onKeyDown(event);
        this.toggleCheckbox();
      }
    }

    toggleCheckbox() {
      const note = this.details.querySelector('ajax-cart-note');
      const input = document.getElementById('ajax-cart__note-status');
      const noteStatus = note.note;
      if (!this.open) {
        input.checked = true;
      } else if (this.open && !noteStatus) {
        input.checked = false;
      }
    }
  }

  window.AccordionNoteItem = AccordionNoteItem;
}

if (!customElements.get('accordion-note')) {
  customElements.define(
    'accordion-note',
    class AccordionNote extends Accordion {
      constructor() {
        super();
      }

      connectedCallback() {
        const accordions = Array.from(this.querySelectorAll('details'));
        this.accordions = accordions.map((accordion) => new AccordionNoteItem(accordion));
        this.currentMediaQuery = p80.mediaQueryService.currentMediaQuery;
        this.onMediaQueryChange();
        this.mediaQueryChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.mediaQueryChange, (event) => {
          if (!event || !event.currentMedia) return;
          this.currentMediaQuery = event.currentMedia;
          this.onMediaQueryChange();
        });
      }
    }
  );
}

if (!customElements.get('ajax-cart-toggle')) {
  customElements.define(
    'ajax-cart-toggle',
    class AjaxCartToggle extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.setAttribute('tabindex', 0);
        this.setAttribute('role', 'button');
        this.handleKeyUp = this.onKeyUp.bind(this);
        this.handleClick = this.onClick.bind(this, false);

        this.addEventListener('keyup', this.handleKeyUp);
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('keyup', this.handleKeyUp);
        this.removeEventListener('click', this.handleClick);
      }

      onClick() {
        const cart = document.querySelector('ajax-cart');
        cart?.isOpen ? cart?.close() : cart?.open();
      }

      onKeyUp(event) {
        if (event?.code === 'Enter') {
          const cart = document.querySelector('ajax-cart');
          cart?.isOpen ? cart?.close() : cart?.open();
        }
      }
    }
  );
}
